/**
 * Remediation Tracker Page — M6.3 Phase 3
 * CRUD, owner/due date/priority, verification-required, escalation, activity log
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import QMSLayout from '../components/QMSLayout';
import {
  Shield, Plus, Filter, Loader2, AlertTriangle, ChevronLeft,
  CheckCircle2, XCircle, Clock, Target, Activity, ArrowRight,
  RefreshCw, Flag, CalendarDays, User, AlertCircle, ShieldCheck,
  ChevronDown, FileText
} from 'lucide-react';

const PRIORITY_CONFIG = {
  critical: { bg: 'bg-red-500/15', text: 'text-red-400', label: 'Critical' },
  high: { bg: 'bg-orange-500/15', text: 'text-orange-400', label: 'High' },
  medium: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', label: 'Medium' },
  low: { bg: 'bg-green-500/15', text: 'text-green-400', label: 'Low' },
};

const STATUS_CONFIG = {
  planned: { bg: 'bg-slate-500/15', text: 'text-slate-400', icon: Clock },
  in_progress: { bg: 'bg-blue-500/15', text: 'text-blue-400', icon: RefreshCw },
  implemented: { bg: 'bg-purple-500/15', text: 'text-purple-400', icon: Target },
  verified: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', icon: CheckCircle2 },
  closed: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: CheckCircle2 },
  deferred: { bg: 'bg-amber-500/15', text: 'text-amber-400', icon: AlertCircle },
};

const VERIFICATION_CONFIG = {
  pending: { bg: 'bg-yellow-500/15', text: 'text-yellow-400' },
  passed: { bg: 'bg-emerald-500/15', text: 'text-emerald-400' },
  failed: { bg: 'bg-red-500/15', text: 'text-red-400' },
};

const Badge = ({ value, config }) => {
  const cfg = config[value] || { bg: 'bg-gray-500/15', text: 'text-gray-400' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.text}`} data-testid={`badge-${value}`}>
      {cfg.label || value?.replace(/_/g, ' ')}
    </span>
  );
};

const isOverdue = (r) => r.due_date && r.due_date < new Date().toISOString() && !['verified', 'closed', 'deferred'].includes(r.status);

// =============== CREATE REMEDIATION MODAL ===============

const CreateRemediationModal = ({ findings, onClose, onCreated }) => {
  const { t } = useTranslation(['governance', 'common']);
  const [findingId, setFindingId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [verificationRequired, setVerificationRequired] = useState(true);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedFinding = findings.find(f => f.finding_id === findingId);

  const handleCreate = async () => {
    if (!findingId) { setError(t('common:messages.selectOption')); return; }
    if (!title.trim()) { setError(t('common:messages.requiredField')); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/governance/remediation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          finding_id: findingId,
          assessment_id: selectedFinding?.assessment_id || '',
          title: title.trim(),
          description: description.trim(),
          owner_name: ownerName.trim(),
          priority,
          due_date: dueDate ? new Date(dueDate).toISOString() : '',
          verification_required: verificationRequired,
          notes: notes.trim(),
        }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || t('common:messages.operationFailed'));
      }
      onCreated(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-remediation-modal">
      <div className="bg-[#1A1F2B] border border-[rgba(255,255,255,0.08)] rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[rgba(255,255,255,0.05)]">
          <h3 className="text-white text-lg font-semibold">{t('governance:remediationCreate.title')}</h3>
          <p className="text-[#8B95A5] text-xs mt-1">{t('governance:remediationCreate.description')}</p>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded p-2" data-testid="create-error">{error}</div>}
          <div>
            <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:remediationCreate.findingLabel')} *</label>
            <select value={findingId} onChange={e => setFindingId(e.target.value)}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm focus:border-[#36A9E1] focus:outline-none"
              data-testid="remediation-finding-select"
            >
              <option value="">{t('governance:remediationCreate.findingPlaceholder')}</option>
              {findings.map(f => (
                <option key={f.finding_id} value={f.finding_id}>
                  {f.finding_id} — {f.control_title || f.control_id} ({f.severity})
                </option>
              ))}
            </select>
          </div>
          {selectedFinding && (
            <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-3 text-xs text-[#8B95A5]">
              <span className="text-[#555]">{t('governance:remediationCreate.assessmentLabel')}:</span> {selectedFinding.assessment_id}
              {selectedFinding.condition_statement && <p className="mt-1">{selectedFinding.condition_statement}</p>}
            </div>
          )}
          <div>
            <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:remediationCreate.titleLabel')} *</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              placeholder={t('governance:remediationCreate.titlePlaceholder')}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm placeholder-[#555] focus:border-[#36A9E1] focus:outline-none"
              data-testid="remediation-title-input"
            />
          </div>
          <div>
            <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:remediationCreate.descriptionLabel')}</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
              placeholder={t('governance:remediationCreate.descriptionPlaceholder')}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm placeholder-[#555] focus:border-[#36A9E1] focus:outline-none resize-none"
              data-testid="remediation-description-input"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:remediationCreate.ownerLabel')}</label>
              <input type="text" value={ownerName} onChange={e => setOwnerName(e.target.value)}
                placeholder={t('governance:remediationCreate.ownerPlaceholder')}
                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm placeholder-[#555] focus:border-[#36A9E1] focus:outline-none"
                data-testid="remediation-owner-input"
              />
            </div>
            <div>
              <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:remediationCreate.priorityLabel')}</label>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm focus:border-[#36A9E1] focus:outline-none"
                data-testid="remediation-priority-select"
              >
                {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:remediationCreate.dueDateLabel')}</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm focus:border-[#36A9E1] focus:outline-none"
                data-testid="remediation-due-date-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={verificationRequired} onChange={e => setVerificationRequired(e.target.checked)}
              className="rounded border-[rgba(255,255,255,0.2)]" data-testid="remediation-verification-checkbox"
            />
            <label className="text-[#8B95A5] text-xs">{t('governance:remediationCreate.verificationLabel')}</label>
          </div>
          <div>
            <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:remediationCreate.notesLabel')}</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder={t('governance:remediationCreate.notesPlaceholder')}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm placeholder-[#555] focus:border-[#36A9E1] focus:outline-none resize-none"
              data-testid="remediation-notes-input"
            />
          </div>
        </div>
        <div className="p-6 border-t border-[rgba(255,255,255,0.05)] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-[#8B95A5] text-sm hover:text-white transition-colors" data-testid="cancel-remediation-btn">{t('common:actions.cancel')}</button>
          <button onClick={handleCreate} disabled={saving}
            className="px-4 py-2 rounded-lg bg-[#36A9E1] text-white text-sm font-medium hover:bg-[#2b8bbf] disabled:opacity-50 transition-colors flex items-center gap-2"
            data-testid="confirm-remediation-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {t('governance:remediationCreate.createButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============== VERIFICATION MODAL ===============

const VerificationModal = ({ remediationId, onClose, onVerified }) => {
  const { t } = useTranslation(['governance', 'common']);
  const [status, setStatus] = useState('passed');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/governance/remediation/${remediationId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ verification_status: status, verification_notes: notes.trim() }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || t('common:messages.operationFailed'));
      }
      onVerified(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="verification-modal">
      <div className="bg-[#1A1F2B] border border-[rgba(255,255,255,0.08)] rounded-xl max-w-md w-full">
        <div className="p-6 border-b border-[rgba(255,255,255,0.05)]">
          <h3 className="text-white text-lg font-semibold">{t('governance:verification.title')}</h3>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded p-2" data-testid="verification-error">{error}</div>}
          <div className="flex gap-3">
            {['passed', 'failed'].map(s => (
              <button key={s} onClick={() => setStatus(s)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${status === s
                  ? s === 'passed' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-red-500/20 text-red-400 border border-red-500/40'
                  : 'bg-[rgba(255,255,255,0.05)] text-[#8B95A5] border border-transparent'}`}
                data-testid={`verify-${s}-btn`}
              >
                {s === 'passed' ? <CheckCircle2 size={14} className="inline mr-1" /> : <XCircle size={14} className="inline mr-1" />}
                {t(`governance:verification.${s}`)}
              </button>
            ))}
          </div>
          <div>
            <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:verification.notesLabel')}</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              placeholder={t('governance:verification.notesPlaceholder')}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm placeholder-[#555] focus:border-[#36A9E1] focus:outline-none resize-none"
              data-testid="verification-notes-input"
            />
          </div>
        </div>
        <div className="p-6 border-t border-[rgba(255,255,255,0.05)] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-[#8B95A5] text-sm hover:text-white transition-colors" data-testid="cancel-verification-btn">{t('common:actions.cancel')}</button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-4 py-2 rounded-lg bg-[#36A9E1] text-white text-sm font-medium hover:bg-[#2b8bbf] disabled:opacity-50 transition-colors flex items-center gap-2"
            data-testid="confirm-verification-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            {t('governance:verification.submitButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============== REMEDIATION DETAIL VIEW ===============

const RemediationDetail = ({ remediationId, onBack }) => {
  const { isAdmin } = useAuth();
  const { t } = useTranslation(['governance', 'common']);
  const [remediation, setRemediation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVerify, setShowVerify] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showEscalation, setShowEscalation] = useState(false);
  const [escalationReason, setEscalationReason] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/governance/remediation/${remediationId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      setRemediation(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [remediationId]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const res = await fetch(`${API}/governance/remediation/${remediationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const d = await res.json();
        toast.error(d.detail || 'Update failed');
        return;
      }
      await load();
    } catch (err) {
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const handleEscalate = async () => {
    try {
      await fetch(`${API}/governance/remediation/${remediationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ escalation_flag: true, escalation_reason: escalationReason }),
      });
      setShowEscalation(false);
      setEscalationReason('');
      await load();
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#36A9E1]" size={32} /></div>;
  if (!remediation) return (
    <div className="qms-card"><div className="qms-card-body p-8 text-center">
      <AlertTriangle className="mx-auto mb-4 text-red-400" size={48} />
      <p className="text-white">{t('governance:remediation.notFound')}</p>
      <button onClick={onBack} className="mt-4 text-[#36A9E1] text-sm hover:underline" data-testid="back-to-list-btn">{t('governance:remediation.backToList')}</button>
    </div></div>
  );

  const statusCfg = STATUS_CONFIG[remediation.status] || STATUS_CONFIG.planned;
  const StatusIcon = statusCfg.icon;
  const overdue = isOverdue(remediation);
  const canVerify = isAdmin && remediation.verification_required && remediation.status === 'implemented' && remediation.verification_status === 'pending';
  const statusFlow = ['planned', 'in_progress', 'implemented'];

  return (
    <div className="space-y-6" data-testid="remediation-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-[#8B95A5] hover:text-white transition-colors" data-testid="back-btn"><ChevronLeft size={20} /></button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-xs font-mono text-[#36A9E1] bg-[rgba(54,169,225,0.1)] px-1.5 py-0.5 rounded">{remediation.remediation_id}</span>
            <Badge value={remediation.status} config={STATUS_CONFIG} />
            <Badge value={remediation.priority} config={PRIORITY_CONFIG} />
            {overdue && <span className="text-xs text-red-400 bg-red-500/15 px-2 py-0.5 rounded" data-testid="overdue-badge">{t('governance:remediation.overdue')}</span>}
            {remediation.escalation_flag && <span className="text-xs text-orange-400 bg-orange-500/15 px-2 py-0.5 rounded flex items-center gap-1" data-testid="escalated-badge"><Flag size={10} /> {t('governance:remediation.escalated')}</span>}
          </div>
          <h2 className="text-white text-lg font-semibold">{remediation.title}</h2>
          <p className="text-[#8B95A5] text-xs mt-0.5">{t('governance:remediation.owner')}: {remediation.owner_name} | {t('governance:remediation.created')}: {new Date(remediation.created_at).toLocaleDateString()}</p>
        </div>
        <button onClick={load} className="text-[#8B95A5] hover:text-[#36A9E1] transition-colors p-1" data-testid="refresh-btn"><RefreshCw size={16} /></button>
      </div>

      {/* Info Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="qms-card"><div className="qms-card-body p-4">
          <div className="text-[#8B95A5] text-xs mb-1">{t('governance:remediation.finding')}</div>
          <div className="text-white text-sm font-mono">{remediation.finding_id}</div>
          <div className="text-[#555] text-xs mt-0.5">{remediation.finding_control_title}</div>
        </div></div>
        <div className="qms-card"><div className="qms-card-body p-4">
          <div className="text-[#8B95A5] text-xs mb-1">{t('governance:remediation.assessment')}</div>
          <div className="text-white text-sm font-mono">{remediation.assessment_id}</div>
          <div className="text-[#555] text-xs mt-0.5">{remediation.assessment_title}</div>
        </div></div>
        <div className="qms-card"><div className="qms-card-body p-4">
          <div className="text-[#8B95A5] text-xs mb-1">{t('governance:remediation.dueDate')}</div>
          <div className={`text-sm font-medium ${overdue ? 'text-red-400' : 'text-white'}`}>
            {remediation.due_date ? new Date(remediation.due_date).toLocaleDateString() : '—'}
          </div>
          {overdue && <div className="text-red-400 text-xs mt-0.5">{t('governance:remediation.overdue')}</div>}
        </div></div>
        <div className="qms-card"><div className="qms-card-body p-4">
          <div className="text-[#8B95A5] text-xs mb-1">{t('governance:remediation.verificationStatus')}</div>
          {remediation.verification_required ? (
            <Badge value={remediation.verification_status} config={VERIFICATION_CONFIG} />
          ) : (
            <span className="text-[#555] text-xs">N/A</span>
          )}
          {remediation.verified_by && <div className="text-[#555] text-xs mt-0.5">{t('governance:remediation.verifiedBy')}: {remediation.verified_by}</div>}
        </div></div>
      </div>

      {/* Description & Notes */}
      {(remediation.description || remediation.notes) && (
        <div className="qms-card"><div className="qms-card-body p-5 space-y-3">
          {remediation.description && (
            <div>
              <h4 className="text-[#8B95A5] text-xs font-medium mb-1">{t('governance:remediation.description')}</h4>
              <p className="text-white text-sm leading-relaxed">{remediation.description}</p>
            </div>
          )}
          {remediation.notes && (
            <div>
              <h4 className="text-[#8B95A5] text-xs font-medium mb-1">{t('governance:remediation.notes')}</h4>
              <p className="text-[#8B95A5] text-sm leading-relaxed">{remediation.notes}</p>
            </div>
          )}
        </div></div>
      )}

      {/* Escalation Section */}
      {remediation.escalation_flag && remediation.escalation_reason && (
        <div className="qms-card border-orange-500/20" data-testid="escalation-section">
          <div className="qms-card-body p-4 flex items-start gap-3">
            <Flag className="text-orange-400 shrink-0 mt-0.5" size={16} />
            <div>
              <h4 className="text-orange-400 text-xs font-medium mb-1">{t('governance:remediation.escalation')}</h4>
              <p className="text-[#8B95A5] text-sm">{remediation.escalation_reason}</p>
            </div>
          </div>
        </div>
      )}

      {/* Action Bar */}
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-2" data-testid="action-bar">
          {statusFlow.includes(remediation.status) && (
            <div className="flex items-center gap-1">
              {statusFlow.map((s, i) => {
                const isActive = s === remediation.status;
                const nextIdx = statusFlow.indexOf(remediation.status) + 1;
                const isNext = i === nextIdx;
                return (
                  <button key={s} onClick={() => isNext && handleStatusChange(s)} disabled={updating || !isNext}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${isActive ? `${STATUS_CONFIG[s].bg} ${STATUS_CONFIG[s].text} border border-current/40` : isNext ? 'bg-[#36A9E1]/15 text-[#36A9E1] border border-[#36A9E1]/40 hover:bg-[#36A9E1]/25 cursor-pointer' : 'bg-[rgba(255,255,255,0.03)] text-[#555] border border-transparent'}`}
                    data-testid={`status-step-${s}`}
                  >
                    {updating && isNext ? <Loader2 size={12} className="animate-spin" /> : null}
                    {t(`governance:remediationStatus.${s}`)}
                    {isNext && <ArrowRight size={10} />}
                  </button>
                );
              })}
            </div>
          )}
          {canVerify && (
            <button onClick={() => setShowVerify(true)}
              className="px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/25 transition-colors flex items-center gap-1"
              data-testid="verify-btn"
            >
              <ShieldCheck size={12} /> {t('governance:verification.title')}
            </button>
          )}
          {!remediation.escalation_flag && !showEscalation && (
            <button onClick={() => setShowEscalation(true)}
              className="px-3 py-1.5 rounded-md text-xs font-medium bg-orange-500/10 text-orange-400 border border-orange-500/30 hover:bg-orange-500/20 transition-colors flex items-center gap-1"
              data-testid="escalate-btn"
            >
              <Flag size={12} /> {t('governance:remediation.escalation')}
            </button>
          )}
        </div>
      )}

      {/* Escalation Input */}
      {showEscalation && (
        <div className="qms-card" data-testid="escalation-form">
          <div className="qms-card-body p-4 flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:remediation.escalationReason')}</label>
              <input type="text" value={escalationReason} onChange={e => setEscalationReason(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm focus:border-[#36A9E1] focus:outline-none"
                data-testid="escalation-reason-input"
              />
            </div>
            <button onClick={handleEscalate} className="px-3 py-2 rounded-lg bg-orange-500/20 text-orange-400 text-xs font-medium" data-testid="confirm-escalation-btn">
              <Flag size={12} className="inline mr-1" /> Escalate
            </button>
            <button onClick={() => setShowEscalation(false)} className="px-3 py-2 rounded-lg text-[#8B95A5] text-xs">{t('common:actions.cancel')}</button>
          </div>
        </div>
      )}

      {/* Activity Log */}
      <div className="qms-card" data-testid="activity-section">
        <div className="qms-card-body p-5">
          <h3 className="text-white text-sm font-semibold flex items-center gap-2 mb-4"><Activity size={14} className="text-[#36A9E1]" /> {t('governance:tabs.activityLog')}</h3>
          {(!remediation.activity || remediation.activity.length === 0) ? (
            <p className="text-[#8B95A5] text-xs">{t('governance:activity.noActivity')}</p>
          ) : (
            <div className="space-y-2">
              {remediation.activity.map((a, idx) => (
                <div key={a.log_id || idx} className="flex items-start gap-3 py-1.5 border-b border-[rgba(255,255,255,0.03)] last:border-0" data-testid={`activity-entry-${idx}`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#36A9E1] mt-1.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-white text-xs"><span className="text-[#36A9E1] font-medium">{a.actor_name}</span> {a.event_type?.replace(/_/g, ' ')}</p>
                    <p className="text-[#555] text-xs mt-0.5">{new Date(a.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showVerify && <VerificationModal remediationId={remediationId} onClose={() => setShowVerify(false)} onVerified={() => { setShowVerify(false); load(); }} />}
    </div>
  );
};

// =============== MAIN PAGE ===============

const RemediationTrackerPage = () => {
  const { isAdmin, isAuditor } = useAuth();
  const { t, i18n } = useTranslation(['governance', 'common']);
  const [remediations, setRemediations] = useState([]);
  const [findings, setFindings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedRemediation, setSelectedRemediation] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (priorityFilter !== 'all') params.set('priority', priorityFilter);
      const qs = params.toString() ? `?${params.toString()}` : '';

      const [rRes, sRes, fRes] = await Promise.all([
        fetch(`${API}/governance/remediation${qs}`, { credentials: 'include' }),
        fetch(`${API}/governance/remediation/stats`, { credentials: 'include' }),
        fetch(`${API}/governance/assessments/stats`, { credentials: 'include' }).catch(() => null),
      ]);
      if (rRes.status === 403) { setError(t('governance:remediation.accessRestrictedDesc')); return; }
      if (!rRes.ok || !sRes.ok) throw new Error('Failed');
      const [rData, sData] = await Promise.all([rRes.json(), sRes.json()]);
      setRemediations(rData);
      setStats(sData);

      // Load open findings for the create modal
      const allFindings = await fetch(`${API}/governance/remediation?status=planned`, { credentials: 'include' }).catch(() => null);
      const findingsRes = await fetch(`${API}/governance/assessments`, { credentials: 'include' });
      if (findingsRes.ok) {
        const assessments = await findingsRes.json();
        const allF = [];
        for (const a of assessments.slice(0, 10)) {
          const fr = await fetch(`${API}/governance/assessments/${a.assessment_id}/findings`, { credentials: 'include' });
          if (fr.ok) {
            const fData = await fr.json();
            allF.push(...fData.filter(f => f.status === 'open' || f.status === 'remediation_planned'));
          }
        }
        setFindings(allF);
      }
    } catch (err) {
      console.error(err);
      setError(t('common:messages.operationFailed'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExportPdf = async () => {
    setExportingPdf(true);
    toast.loading(t('governance:pdfExport.exportingPdf'), { id: 'pdf-export' });
    try {
      const locale = i18n.language || 'en';
      const res = await fetch(`${API}/governance/remediation/export/pdf?locale=${locale}`, { credentials: 'include' });
      if (!res.ok) { const d = await res.json().catch(() => ({})); toast.error(d.detail || t('governance:pdfExport.exportFailed'), { id: 'pdf-export' }); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CHARSTAN_Remediation_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('governance:pdfExport.downloadStarted'), { id: 'pdf-export' });
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error(t('governance:pdfExport.exportFailed'), { id: 'pdf-export' });
    } finally {
      setExportingPdf(false);
    }
  };

  if (!isAdmin && !isAuditor) {
    return (
      <QMSLayout title={t('governance:remediation.pageTitle')}>
        <div className="qms-card"><div className="qms-card-body p-8 text-center">
          <Shield className="mx-auto mb-4 text-red-400" size={48} />
          <h3 className="text-white font-semibold mb-2">{t('governance:remediation.accessRestricted')}</h3>
          <p className="text-[#8B95A5] text-sm">{t('governance:remediation.accessRestrictedDesc')}</p>
        </div></div>
      </QMSLayout>
    );
  }

  if (selectedRemediation) {
    return (
      <QMSLayout title={t('governance:remediation.pageTitle')}>
        <RemediationDetail remediationId={selectedRemediation} onBack={() => { setSelectedRemediation(null); loadData(); }} />
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title={t('governance:remediation.pageTitle')}>
      <div className="space-y-6" data-testid="remediation-tracker-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-white text-xl font-bold">{t('governance:remediation.pageTitle')}</h1>
            <p className="text-[#8B95A5] text-xs mt-1">{t('governance:remediation.pageDescription')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportPdf}
              disabled={exportingPdf || loading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#C9A84C]/20 text-[#C9A84C] text-sm font-medium hover:bg-[#C9A84C]/30 disabled:opacity-50 transition-colors border border-[#C9A84C]/30"
              data-testid="export-remediation-pdf-btn"
            >
              {exportingPdf ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              {exportingPdf ? t('governance:pdfExport.exportingPdf') : t('governance:pdfExport.exportPdf')}
            </button>
            <button onClick={() => setShowCreate(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#36A9E1] text-white text-sm font-medium hover:bg-[#2b8bbf] transition-colors"
              data-testid="new-remediation-btn"
            >
              <Plus size={16} /> {t('governance:remediation.newRemediation')}
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="remediation-stats">
            <div className="qms-card"><div className="qms-card-body p-3 text-center">
              <div className="text-white text-xl font-bold" data-testid="stat-total">{stats.total}</div>
              <div className="text-[#8B95A5] text-xs">{t('governance:remediation.total')}</div>
            </div></div>
            <div className="qms-card"><div className="qms-card-body p-3 text-center">
              <div className="text-blue-400 text-xl font-bold" data-testid="stat-in-progress">{stats.status_counts?.in_progress || 0}</div>
              <div className="text-[#8B95A5] text-xs">{t('governance:remediation.inProgress')}</div>
            </div></div>
            <div className="qms-card"><div className="qms-card-body p-3 text-center">
              <div className="text-emerald-400 text-xl font-bold" data-testid="stat-verified">{stats.status_counts?.verified || 0}</div>
              <div className="text-[#8B95A5] text-xs">{t('governance:remediation.verified')}</div>
            </div></div>
            <div className="qms-card"><div className="qms-card-body p-3 text-center">
              <div className="text-red-400 text-xl font-bold" data-testid="stat-overdue">{stats.overdue}</div>
              <div className="text-[#8B95A5] text-xs">{t('governance:remediation.overdue')}</div>
            </div></div>
            <div className="qms-card"><div className="qms-card-body p-3 text-center">
              <div className="text-orange-400 text-xl font-bold" data-testid="stat-escalated">{stats.escalated}</div>
              <div className="text-[#8B95A5] text-xs">{t('governance:remediation.escalated')}</div>
            </div></div>
            <div className="qms-card"><div className="qms-card-body p-3 text-center">
              <div className="text-purple-400 text-xl font-bold" data-testid="stat-verification-pending">{stats.verification_pending}</div>
              <div className="text-[#8B95A5] text-xs">{t('governance:remediation.verificationPending')}</div>
            </div></div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3">
          <Filter size={14} className="text-[#8B95A5]" />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none"
            data-testid="status-filter"
          >
            <option value="all">{t('governance:remediation.allStatuses')}</option>
            {Object.keys(STATUS_CONFIG).map(k => <option key={k} value={k}>{t(`governance:remediationStatus.${k}`)}</option>)}
          </select>
          <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}
            className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none"
            data-testid="priority-filter"
          >
            <option value="all">{t('governance:remediation.allPriorities')}</option>
            {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={loadData} className="text-[#8B95A5] hover:text-[#36A9E1] transition-colors p-1" data-testid="refresh-list-btn"><RefreshCw size={14} /></button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#36A9E1]" size={32} /></div>
        ) : error ? (
          <div className="qms-card"><div className="qms-card-body p-8 text-center">
            <AlertTriangle className="mx-auto mb-4 text-red-400" size={48} />
            <p className="text-red-400 text-sm">{error}</p>
          </div></div>
        ) : remediations.length === 0 ? (
          <div className="qms-card"><div className="qms-card-body p-12 text-center">
            <Target className="mx-auto mb-4 text-[#555]" size={48} />
            <h3 className="text-white font-semibold mb-2">{t('governance:remediation.noRemediationsTitle')}</h3>
            <p className="text-[#8B95A5] text-sm mb-4">{t('governance:remediation.noRemediationsDesc')}</p>
          </div></div>
        ) : (
          <div className="space-y-3">
            {remediations.map(r => {
              const overdue = isOverdue(r);
              return (
                <div key={r.remediation_id} className={`qms-card hover:border-[rgba(54,169,225,0.2)] transition-colors cursor-pointer ${overdue ? 'border-red-500/20' : ''} ${r.escalation_flag ? 'border-orange-500/20' : ''}`}
                  onClick={() => setSelectedRemediation(r.remediation_id)}
                  data-testid={`remediation-card-${r.remediation_id}`}
                >
                  <div className="qms-card-body p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs font-mono text-[#36A9E1] bg-[rgba(54,169,225,0.1)] px-1.5 py-0.5 rounded">{r.remediation_id}</span>
                          <Badge value={r.status} config={STATUS_CONFIG} />
                          <Badge value={r.priority} config={PRIORITY_CONFIG} />
                          {overdue && <span className="text-xs text-red-400 bg-red-500/15 px-1.5 py-0.5 rounded">{t('governance:remediation.overdue')}</span>}
                          {r.escalation_flag && <span className="text-xs text-orange-400 bg-orange-500/15 px-1.5 py-0.5 rounded flex items-center gap-1"><Flag size={10} /> {t('governance:remediation.escalated')}</span>}
                          {r.verification_required && r.verification_status === 'pending' && r.status === 'implemented' && (
                            <span className="text-xs text-purple-400 bg-purple-500/15 px-1.5 py-0.5 rounded">{t('governance:remediation.verificationPending')}</span>
                          )}
                        </div>
                        <h3 className="text-white text-sm font-medium">{r.title}</h3>
                        <div className="flex items-center gap-4 mt-2 text-[#555] text-xs">
                          <span>{t('governance:remediation.owner')}: {r.owner_name}</span>
                          <span>{t('governance:remediation.finding')}: {r.finding_id}</span>
                          {r.due_date && <span className={overdue ? 'text-red-400' : ''}>{t('governance:remediation.dueDate')}: {new Date(r.due_date).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      <ArrowRight size={16} className="text-[#555] shrink-0 mt-2" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showCreate && (
          <CreateRemediationModal
            findings={findings}
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); loadData(); }}
          />
        )}
      </div>
    </QMSLayout>
  );
};

export default RemediationTrackerPage;
